/* ============================================================
   Dymona Shopify Theme — Consolidated JS bundle
   Self-contained IIFE. No ES modules (browser-compat for Shopify).

   Adapted from v0 sources:
   - src/lib/cart.js  → CartAPI (Shopify Cart AJAX endpoints)
   - src/lib/store.js → Store
   - src/scripts/main.js → bootstrap
   - All src/sections/<name>/<name>.js → setup<Name>(root)

   Differences from v0:
   - cart.js localStorage → fetch /cart/*.js
   - product.js JSON file → reads <script type="application/json"> in Liquid
   - GSAP / Lenis loaded via CDN (see theme.liquid)
   ============================================================ */
(function () {
  'use strict';

  // ============================================================
  // Store (pub/sub state)
  // ============================================================
  class Store {
    constructor(initial) {
      this.state = Object.assign({}, initial);
      this.listeners = new Map();
    }
    get(key) { return key === undefined ? this.state : this.state[key]; }
    set(key, value) {
      if (typeof key === 'object' && key !== null) {
        Object.assign(this.state, key);
        for (const k of Object.keys(key)) this._emit(k);
      } else {
        this.state[key] = value;
        this._emit(key);
      }
    }
    subscribe(key, cb) {
      if (!this.listeners.has(key)) this.listeners.set(key, new Set());
      this.listeners.get(key).add(cb);
      return () => this.listeners.get(key) && this.listeners.get(key).delete(cb);
    }
    _emit(key) {
      const set = this.listeners.get(key);
      if (set) for (const cb of set) { try { cb(this.state); } catch (e) { console.error('[store]', e); } }
    }
  }
  const store = new Store({
    cart: { items: [], item_count: 0, items_subtotal_price: 0, total_price: 0, currency: 'EUR' },
    drawer: { cart: false, menu: false, newsletter: false }
  });

  // ============================================================
  // Money formatter (matches v0 — €98.99 style)
  // ============================================================
  const SYMBOLS = { EUR: '€', USD: '$', GBP: '£' };
  function money(cents, currency) {
    currency = currency || 'EUR';
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    return (SYMBOLS[currency] || currency) + (cents / 100).toFixed(2);
  }

  // ============================================================
  // Cart API — Shopify Cart AJAX wrapper
  // ============================================================
  const routes = (window.theme && window.theme.routes) || {
    cart_url: '/cart',
    cart_add_url: '/cart/add.js',
    cart_change_url: '/cart/change.js',
    cart_update_url: '/cart/update.js',
    cart_clear_url: '/cart/clear.js'
  };

  async function fetchJson(url, options) {
    const opts = Object.assign({ headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } }, options || {});
    const res = await fetch(url, opts);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.description || data.message || res.statusText), { response: data, status: res.status });
    return data;
  }

  const cart = {
    async get() {
      const data = await fetchJson(routes.cart_url + '.js');
      store.set('cart', data);
      return data;
    },
    async add(items) {
      const list = Array.isArray(items) ? items : [items];
      const data = await fetchJson(routes.cart_add_url, {
        method: 'POST',
        body: JSON.stringify({ items: list })
      });
      await this.get();
      track('Cart.Add', { items: list });
      return data;
    },
    async update(updates) {
      const data = await fetchJson(routes.cart_update_url, {
        method: 'POST',
        body: JSON.stringify(typeof updates === 'object' && !Array.isArray(updates) ? { updates: updates } : { updates: { _legacy: updates } })
      });
      store.set('cart', data);
      track('Cart.Update', { updates: updates });
      return data;
    },
    async change(payload) {
      const data = await fetchJson(routes.cart_change_url, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      store.set('cart', data);
      track('Cart.Change', payload);
      return data;
    },
    async clear() {
      const data = await fetchJson(routes.cart_clear_url, { method: 'POST' });
      store.set('cart', data);
      track('Cart.Clear');
      return data;
    },
    checkout() {
      track('Cart.Checkout');
      window.location.href = routes.cart_url; // Shopify cart page → Checkout button
    }
  };

  // Initial cart hydrate
  cart.get().catch(() => {/* ignore on first hit */});

  // ============================================================
  // Analytics (placeholder — wire to GA4 / Klaviyo in v1.1)
  // ============================================================
  function track(event, props) {
    if (window.console && window.console.log) console.log('%c[track] ' + event, 'color:#6b6b6b', props || {});
    // v1.1: gtag('event', event, props);
    // v1.1: window.klaviyo && window.klaviyo.track(event, props);
  }
  function captureEmail(email, meta) {
    track('Newsletter.Submit', Object.assign({ email: email }, meta || {}));
    // v1.1: klaviyo.identify({ $email: email });
    return Promise.resolve({ ok: true });
  }

  // ============================================================
  // Toast notifications (window.dispatchEvent('dymona:toast', ...))
  // ============================================================
  function initToasts() {
    let el = null, timer = null;
    window.addEventListener('dymona:toast', (e) => {
      const { message, duration = 2800 } = e.detail || {};
      if (!message) return;
      if (!el) {
        el = document.createElement('div');
        el.className = 'toast';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        document.body.appendChild(el);
      }
      el.textContent = message;
      requestAnimationFrame(() => el.classList.add('toast--visible'));
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('toast--visible'), duration);
    });
  }

  // ============================================================
  // Demo badge
  // ============================================================
  function injectDemoBadge() {
    if (document.querySelector('.demo-badge')) return;
    const badge = document.createElement('div');
    badge.className = 'demo-badge';
    badge.textContent = 'Demo · v0.1';
    document.body.appendChild(badge);
  }

  // ============================================================
  // Smooth scroll (Lenis loaded via CDN; fallback noop if missing)
  // ============================================================
  function initLenis() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof window.Lenis !== 'function') return;
    const lenis = new window.Lenis({ duration: 1.1, smoothWheel: true, smoothTouch: false });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((time) => lenis.raf(time * 1000));
      window.gsap.ticker.lagSmoothing(0);
    }
    window.__lenis = lenis;
  }

  // ============================================================
  // Silence benign View Transition aborts
  // ============================================================
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.name === 'AbortError' && /Transition was (skipped|aborted)/.test(e.reason.message || '')) e.preventDefault();
  });

  // ============================================================
  // Section: Header — scroll state, cart icon, active nav, count pulse
  // ============================================================
  function setupHeader(root) {
    if (!root) return;
    const cartBtn = root.querySelector('[data-cart-toggle]');
    const cartCount = root.querySelector('[data-cart-count]');
    const menuBtn = root.querySelector('[data-menu-toggle]');
    const navLinks = root.querySelectorAll('[data-nav-link]');

    const updateScroll = () => root.setAttribute('data-scrolled', window.scrollY > 8 ? 'true' : 'false');
    updateScroll();
    window.addEventListener('scroll', updateScroll, { passive: true });

    let lastCount = (store.get('cart').item_count) || 0;
    store.subscribe('cart', (state) => {
      const count = state.cart.item_count || 0;
      if (cartCount) cartCount.textContent = String(count);
      if (cartBtn) cartBtn.setAttribute('data-has-items', count > 0 ? 'true' : 'false');
      if (cartCount && count !== lastCount) {
        cartCount.classList.remove('is-pulsing');
        void cartCount.offsetWidth;
        cartCount.classList.add('is-pulsing');
        setTimeout(() => cartCount.classList.remove('is-pulsing'), 600);
      }
      lastCount = count;
    });

    cartBtn && cartBtn.addEventListener('click', () => store.set('drawer', Object.assign({}, store.get('drawer'), { cart: true })));

    // ---- Mobile menu drawer ----
    // Open/close state lives on the header element. CSS reacts via the
    // [data-menu-open="true"] attribute selector. JS additionally:
    //   - locks body scroll while open
    //   - closes on ESC
    //   - closes on nav-link tap (so navigating doesn't leave the drawer open)
    const setMenuOpen = (next) => {
      const d = store.get('drawer');
      store.set('drawer', Object.assign({}, d, { menu: next }));
      root.setAttribute('data-menu-open', String(next));
      if (menuBtn) {
        menuBtn.setAttribute('aria-expanded', String(next));
        menuBtn.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
      }
      document.documentElement.classList.toggle('scroll-locked', next);
    };
    menuBtn && menuBtn.addEventListener('click', () => {
      const isOpen = root.getAttribute('data-menu-open') === 'true';
      setMenuOpen(!isOpen);
    });
    // Tap a link inside the drawer → close the drawer (real nav happens via href)
    navLinks.forEach((link) => {
      link.addEventListener('click', () => {
        if (root.getAttribute('data-menu-open') === 'true') setMenuOpen(false);
      });
    });
    // ESC closes the drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && root.getAttribute('data-menu-open') === 'true') {
        setMenuOpen(false);
        menuBtn && menuBtn.focus();
      }
    });
    // Resize across the mobile/desktop breakpoint while drawer is open → close it
    const drawerMq = window.matchMedia('(min-width: 768px)');
    const onDrawerMq = (e) => { if (e.matches && root.getAttribute('data-menu-open') === 'true') setMenuOpen(false); };
    if (drawerMq.addEventListener) drawerMq.addEventListener('change', onDrawerMq);
    else if (drawerMq.addListener) drawerMq.addListener(onDrawerMq);

    // Active nav link
    const path = window.location.pathname;
    navLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href && (href === path || (href !== '/' && path.indexOf(href) === 0))) link.setAttribute('aria-current', 'page');
    });
  }

  // ============================================================
  // Section: Hero — GSAP timeline + callouts reveal
  // ============================================================
  // ============================================================
  // Section: Hero — full-width carousel
  // Pure CSS cross-fade between slides. JS handles:
  //   - autoplay + pause on hover + pause out-of-viewport
  //   - prev/next buttons + dots + touch swipe + keyboard arrows
  //   - active-slide video playback + callout reveal
  // Single-slide hero short-circuits the carousel logic.
  // ============================================================
  function setupHero(root) {
    if (!root) return;

    const showCallouts = (slide) => {
      if (!slide) return;
      slide.querySelectorAll('[data-hero-callout]').forEach((c) => c.classList.add('callout--visible'));
    };
    const hideCallouts = (slide) => {
      if (!slide) return;
      slide.querySelectorAll('[data-hero-callout]').forEach((c) => c.classList.remove('callout--visible'));
    };

    const slides = [...root.querySelectorAll('[data-hero-slide]')];
    if (!slides.length) return;
    const activeOnLoad = slides.find((s) => s.dataset.active === 'true') || slides[0];
    showCallouts(activeOnLoad);

    // Single slide → done (no controls to wire up)
    if (slides.length === 1) return;

    const dots = [...root.querySelectorAll('[data-hero-dot]')];
    const prevBtn = root.querySelector('[data-hero-prev]');
    const nextBtn = root.querySelector('[data-hero-next]');
    const carousel = root.querySelector('[data-hero-carousel]');

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = root.dataset.autoplay === 'true' && !reduce;
    const interval = (parseInt(root.dataset.interval, 10) || 6) * 1000;

    let current = slides.indexOf(activeOnLoad);
    if (current < 0) current = 0;
    let timer = null;

    function go(idx) {
      const target = ((idx % slides.length) + slides.length) % slides.length;
      if (target === current) return;
      const prev = current;
      current = target;

      slides[prev].removeAttribute('data-active');
      slides[current].setAttribute('data-active', 'true');

      dots.forEach((d, i) => {
        if (i === current) {
          d.setAttribute('data-active', 'true');
          d.setAttribute('aria-selected', 'true');
        } else {
          d.removeAttribute('data-active');
          d.setAttribute('aria-selected', 'false');
        }
      });

      // Videos: only the active slide plays (saves CPU + battery)
      slides.forEach((s, i) => {
        const v = s.querySelector('video');
        if (!v) return;
        if (i === current) v.play().catch(() => {});
        else v.pause();
      });

      hideCallouts(slides[prev]);
      showCallouts(slides[current]);
    }

    const next = () => go(current + 1);
    const prev = () => go(current - 1);

    function startTimer() {
      if (!autoplay || timer) return;
      timer = setInterval(next, interval);
    }
    function stopTimer() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function restart() { stopTimer(); startTimer(); }

    if (prevBtn) prevBtn.addEventListener('click', () => { prev(); restart(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { next(); restart(); });
    dots.forEach((d, i) => d.addEventListener('click', () => { go(i); restart(); }));

    // Pause on hover / focus
    root.addEventListener('mouseenter', stopTimer);
    root.addEventListener('mouseleave', startTimer);
    root.addEventListener('focusin', stopTimer);
    root.addEventListener('focusout', startTimer);

    // Pause when scrolled out of viewport
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { e.isIntersecting ? startTimer() : stopTimer(); });
      }, { threshold: 0.1 });
      io.observe(root);
    }

    // Touch swipe (mobile)
    if (carousel) {
      let touchStartX = null;
      carousel.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        stopTimer();
      }, { passive: true });
      carousel.addEventListener('touchend', (e) => {
        if (touchStartX == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
        touchStartX = null;
        startTimer();
      }, { passive: true });
    }

    // Keyboard arrows
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { prev(); restart(); }
      else if (e.key === 'ArrowRight') { next(); restart(); }
    });

    startTimer();
  }

  // ============================================================
  // Section: Value Trio — fade up on scroll
  // ============================================================
  function setupValueTrio(root) {
    if (!root || !window.gsap || !window.ScrollTrigger) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const items = root.querySelectorAll('.section-value-trio__item');
    if (!items.length) return;
    window.gsap.from(items, {
      opacity: 0, y: 36, duration: 0.9, stagger: 0.12, ease: 'expo.out',
      scrollTrigger: { trigger: root, start: 'top 75%', toggleActions: 'play none none none' }
    });
  }

  // ============================================================
  // Section: Component Story — ScrollTrigger pin + 4-frame cycle
  // ============================================================
  function setupComponentStory(root) {
    if (!root) return;
    const pin = root.querySelector('[data-cs-pin]');
    const frames = root.querySelectorAll('[data-cs-frame]');
    const panels = root.querySelectorAll('[data-cs-panel]');
    const dots = root.querySelectorAll('[data-cs-dot]');
    const progressFill = root.querySelector('[data-cs-progress-fill]');
    if (!frames.length || !panels.length) return;
    const total = frames.length;
    const activate = (i) => {
      frames.forEach((f, idx) => f.setAttribute('data-active', idx === i ? 'true' : 'false'));
      panels.forEach((p, idx) => p.setAttribute('data-active', idx === i ? 'true' : 'false'));
      dots.forEach((d, idx) => d.setAttribute('data-active', idx === i ? 'true' : 'false'));
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobile = window.matchMedia('(max-width: 1023px)').matches;

    // ---- Reduce-motion / no-GSAP fallback ----
    // Show first frame + dots remain tappable. No scroll-triggered animation.
    if (reduce || !window.gsap || !window.ScrollTrigger) {
      dots.forEach((d, i) => d.addEventListener('click', () => {
        activate(i);
        if (progressFill) progressFill.style.transform = 'scaleX(' + ((i + 1) / total) + ')';
      }));
      return;
    }

    // ---- ScrollTrigger pin + scrub (desktop AND mobile) ----
    // Same wow factor on both breakpoints. Mobile CSS restructures the
    // pin region to a single column (image top, text below) — see the
    // mobile @media block in theme.css "Section: Component Story · MOBILE LAYOUT".
    // Mobile gets a slightly shorter scroll distance so the experience
    // doesn't feel like a never-ending pin.
    // lastStep guard prevents activate() from re-firing every scroll
    // frame (would interrupt CSS opacity/transform transitions, leaving
    // panels stuck mid-fade with two overlapping texts).
    let lastStep = -1;
    const stretchFactor = mobile ? 0.7 : 0.9;
    const st = window.ScrollTrigger.create({
      trigger: pin, start: 'top top',
      end: () => '+=' + (window.innerHeight * (total - 1) * stretchFactor),
      pin: pin, pinSpacing: true, scrub: 0.4,
      onUpdate: (self) => {
        const step = Math.min(total - 1, Math.floor(self.progress * total));
        if (step !== lastStep) {
          activate(step);
          if (progressFill) progressFill.style.transform = 'scaleX(' + ((step + 1) / total) + ')';
          lastStep = step;
        }
      }
    });
    dots.forEach((d, i) => d.addEventListener('click', () => {
      const target = st.start + (st.end - st.start) * (i / (total - 1));
      window.scrollTo({ top: target, behavior: 'smooth' });
    }));
  }

  // ============================================================
  // Section: Product Cards — fade up on scroll
  // ============================================================
  function setupProductCards(root) {
    if (!root || !window.gsap || !window.ScrollTrigger) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const cards = root.querySelectorAll('[data-product-card]');
    if (!cards.length) return;
    window.gsap.from(cards, {
      opacity: 0, y: 40, duration: 0.8, stagger: 0.15, ease: 'expo.out',
      scrollTrigger: { trigger: root, start: 'top 70%', toggleActions: 'play none none none' }
    });
  }

  // ============================================================
  // Section: Newsletter (inline)
  // ============================================================
  function setupNewsletter(root) {
    if (!root) return;
    const form = root.querySelector('[data-newsletter-form]');
    const input = root.querySelector('[data-newsletter-input]');
    const submit = root.querySelector('[data-newsletter-submit]');
    const result = root.querySelector('[data-newsletter-result]');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        result.textContent = 'Please enter a valid email.';
        result.setAttribute('data-state', 'error');
        return;
      }
      submit.setAttribute('data-loading', 'true');
      submit.disabled = true;
      try {
        await captureEmail(email, { source: 'inline-newsletter' });
        result.textContent = 'Thanks. Check your inbox for the code.';
        result.setAttribute('data-state', 'success');
        input.value = '';
      } catch (err) {
        result.textContent = 'Something went wrong. Please try again.';
        result.setAttribute('data-state', 'error');
      } finally {
        submit.removeAttribute('data-loading');
        submit.disabled = false;
      }
    });
  }

  // ============================================================
  // Section: Product Gallery — thumbnail swap
  // ============================================================
  function setupProductGallery(root) {
    if (!root) return;
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = root.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        if (src && main) { const img = main.querySelector('img'); if (img) img.src = src; }
        thumbs.forEach((t) => t.setAttribute('data-active', t === thumb ? 'true' : 'false'));
      });
    });
  }

  // ============================================================
  // Section: Product Info — variant select + add to cart
  // ============================================================
  function setupProductInfo(root) {
    if (!root) return;
    const swatches = root.querySelectorAll('[data-variant-swatch]');
    const variantSelectedLabel = root.querySelector('[data-variant-selected]');
    const priceEl = root.querySelector('[data-product-price]');
    const addBtn = root.querySelector('[data-add-to-cart]');

    let selectedVariantId = swatches[0] ? Number(swatches[0].dataset.variantId) : null;

    swatches.forEach((sw) => {
      sw.addEventListener('click', () => {
        selectedVariantId = Number(sw.dataset.variantId);
        swatches.forEach((s) => s.setAttribute('aria-pressed', String(s === sw)));
        if (variantSelectedLabel) variantSelectedLabel.textContent = sw.textContent.trim();
        // Update price if variant has different price (read from data-variant-price cents)
        const variantPrice = sw.dataset.variantPrice;
        if (priceEl && variantPrice) priceEl.textContent = money(Number(variantPrice));
      });
    });

    if (swatches[0]) swatches[0].setAttribute('aria-pressed', 'true');

    addBtn && addBtn.addEventListener('click', async () => {
      if (!selectedVariantId) return;
      addBtn.setAttribute('data-loading', 'true');
      try {
        await cart.add({ id: selectedVariantId, quantity: 1 });
        window.dispatchEvent(new CustomEvent('dymona:toast', { detail: { message: 'Added to cart.' } }));
        store.set('drawer', Object.assign({}, store.get('drawer'), { cart: true }));
      } catch (e) {
        window.dispatchEvent(new CustomEvent('dymona:toast', { detail: { message: (e.response && e.response.description) || 'Sorry, something went wrong.' } }));
      } finally {
        addBtn.removeAttribute('data-loading');
      }
    });

    // --- Sticky add-to-cart bar ---
    // When the main Add-to-cart scrolls out of view, slide up a bottom
    // bar that mirrors the same price + a CTA forwarding to the main btn.
    const stickyBar = document.querySelector('[data-sticky-atc]');
    const stickyPrice = stickyBar && stickyBar.querySelector('[data-sticky-atc-price]');
    const stickyCTA = stickyBar && stickyBar.querySelector('[data-sticky-atc-cta]');

    if (stickyBar && addBtn && 'IntersectionObserver' in window) {
      // Toggle visibility based on main CTA's visibility
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            stickyBar.removeAttribute('data-visible');
            stickyBar.setAttribute('aria-hidden', 'true');
          } else {
            // Only show if user has scrolled PAST the main CTA (not above it).
            // boundingClientRect.top < 0 means the CTA is above the viewport.
            if (e.boundingClientRect.top < 0) {
              stickyBar.setAttribute('data-visible', 'true');
              stickyBar.removeAttribute('aria-hidden');
            }
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
      io.observe(addBtn);

      // Mirror price updates from variant swatches
      if (priceEl && stickyPrice) {
        const mo = new MutationObserver(() => { stickyPrice.textContent = priceEl.textContent; });
        mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
      }

      // Forward sticky CTA click to the main add-to-cart handler
      if (stickyCTA) {
        stickyCTA.addEventListener('click', (e) => {
          e.preventDefault();
          addBtn.click();
        });
      }
    }
  }

  // ============================================================
  // Section: Spec Table — counter animation from JSON data attribute
  // ============================================================
  function setupSpecTable(root) {
    if (!root) return;
    if (!window.gsap || !window.ScrollTrigger || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Just set final values (already in HTML)
      return;
    }
    window.ScrollTrigger.create({
      trigger: root, start: 'top 70%', once: true,
      onEnter: () => {
        root.querySelectorAll('[data-count-target]').forEach((el) => {
          const target = Number(el.dataset.countTarget);
          const format = el.dataset.countFormat || 'number';
          if (!isFinite(target)) return;
          const obj = { v: 0 };
          window.gsap.to(obj, {
            v: target, duration: 1.6, ease: 'expo.out',
            onUpdate: () => {
              if (format === 'decimal') el.textContent = obj.v.toFixed(2);
              else el.textContent = Math.round(obj.v).toLocaleString('en-US');
            }
          });
        });
      }
    });
  }

  // ============================================================
  // Section: FAQ Accordion
  // ============================================================
  function setupFaqAccordion(root) {
    if (!root) return;
    const items = root.querySelectorAll('[data-faq-item]');
    items.forEach((item) => {
      const button = item.querySelector('[data-faq-button]');
      const answer = item.querySelector('[data-faq-answer]');
      if (!button || !answer) return;
      button.addEventListener('click', () => {
        const isOpen = item.getAttribute('data-open') === 'true';
        items.forEach((o) => {
          const a = o.querySelector('[data-faq-answer]');
          o.setAttribute('data-open', 'false');
          const b = o.querySelector('[data-faq-button]');
          b && b.setAttribute('aria-expanded', 'false');
          if (a) a.style.maxHeight = '0';
        });
        if (!isOpen) {
          item.setAttribute('data-open', 'true');
          button.setAttribute('aria-expanded', 'true');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });
  }

  // ============================================================
  // Cart Drawer
  // ============================================================
  function setupCartDrawer(root) {
    if (!root) return;
    const backdrop = root.querySelector('[data-cart-backdrop]');
    const closers = root.querySelectorAll('[data-cart-close]');
    const itemsEl = root.querySelector('[data-cart-items]');
    const emptyEl = root.querySelector('[data-cart-empty]');
    const footerEl = root.querySelector('[data-cart-footer]');
    const subtotalEl = root.querySelector('[data-cart-subtotal]');
    const checkoutTotalEl = root.querySelector('[data-cart-checkout-total]');
    const checkoutBtn = root.querySelector('[data-cart-checkout]');
    const progressFill = root.querySelector('[data-cart-progress-fill]');
    const progressText = root.querySelector('[data-cart-progress-text]');
    const THRESHOLD = (window.theme && window.theme.settings && window.theme.settings.free_shipping_threshold) || 5000;

    const open = () => { root.setAttribute('data-open', 'true'); root.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; };
    const close = () => { root.setAttribute('data-open', 'false'); root.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; };

    backdrop && backdrop.addEventListener('click', () => store.set('drawer', Object.assign({}, store.get('drawer'), { cart: false })));
    closers.forEach((c) => c.addEventListener('click', () => store.set('drawer', Object.assign({}, store.get('drawer'), { cart: false }))));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.getAttribute('data-open') === 'true') store.set('drawer', Object.assign({}, store.get('drawer'), { cart: false })); });

    store.subscribe('drawer', (state) => { state.drawer.cart ? open() : close(); });

    const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));

    const render = (state) => {
      const items = state.cart.items || [];
      const subtotal = state.cart.items_subtotal_price || state.cart.total_price || 0;
      if (items.length === 0) {
        emptyEl.style.display = '';
        itemsEl.hidden = true;
        footerEl.hidden = true;
        return;
      }
      emptyEl.style.display = 'none';
      itemsEl.hidden = false;
      footerEl.hidden = false;
      itemsEl.innerHTML = items.map((li) => (
        '<li class="cart-drawer__item" data-line-key="' + escapeHtml(li.key) + '">' +
          '<div class="cart-drawer__item-media">' +
            (li.image ? '<img src="' + escapeHtml(li.image) + '" alt="' + escapeHtml(li.product_title || li.title) + '" loading="lazy" onerror="this.style.display=\'none\'"/>' : '') +
          '</div>' +
          '<div class="cart-drawer__item-info">' +
            '<div class="cart-drawer__item-title">' + escapeHtml(li.product_title || li.title) + '</div>' +
            '<div class="cart-drawer__item-variant">' + escapeHtml(li.variant_title || '') + '</div>' +
            '<div class="cart-drawer__item-controls">' +
              '<div class="cart-drawer__qty">' +
                '<button type="button" data-line-decrease aria-label="Decrease">−</button>' +
                '<span class="cart-drawer__qty-value">' + li.quantity + '</span>' +
                '<button type="button" data-line-increase aria-label="Increase">+</button>' +
              '</div>' +
              '<div class="cart-drawer__item-price">' + money(li.final_line_price || li.line_price || (li.final_price * li.quantity)) + '</div>' +
            '</div>' +
            '<button type="button" class="cart-drawer__item-remove" data-line-remove>Remove</button>' +
          '</div>' +
        '</li>'
      )).join('');
      subtotalEl.textContent = money(subtotal);
      if (checkoutTotalEl) checkoutTotalEl.textContent = money(subtotal);
      const pct = Math.min(100, (subtotal / THRESHOLD) * 100);
      if (progressFill) progressFill.style.width = pct + '%';
      if (progressText) {
        if (subtotal >= THRESHOLD) progressText.textContent = 'Free EU shipping unlocked.';
        else progressText.textContent = 'Add ' + money(THRESHOLD - subtotal) + ' more for free EU shipping.';
      }
      itemsEl.querySelectorAll('[data-line-key]').forEach((li) => {
        const key = li.dataset.lineKey;
        const item = items.find((i) => i.key === key);
        if (!item) return;
        li.querySelector('[data-line-increase]') && li.querySelector('[data-line-increase]').addEventListener('click', () => cart.change({ id: item.key, quantity: item.quantity + 1 }));
        li.querySelector('[data-line-decrease]') && li.querySelector('[data-line-decrease]').addEventListener('click', () => cart.change({ id: item.key, quantity: item.quantity - 1 }));
        li.querySelector('[data-line-remove]') && li.querySelector('[data-line-remove]').addEventListener('click', () => cart.change({ id: item.key, quantity: 0 }));
      });
    };

    store.subscribe('cart', render);
    render(store.get());
    checkoutBtn && checkoutBtn.addEventListener('click', () => cart.checkout());
  }

  // ============================================================
  // Newsletter Modal
  // ============================================================
  function setupNewsletterModal(root) {
    if (!root) return;
    const STORAGE_KEY = 'dymona.newsletter.dismiss';
    const COOLDOWN_DAYS = (window.theme && window.theme.settings && window.theme.settings.newsletter_cooldown_days) || 14;
    const DWELL = (window.theme && window.theme.settings && window.theme.settings.newsletter_dwell_seconds) || 25;

    const inCooldown = () => {
      try {
        const stamp = localStorage.getItem(STORAGE_KEY);
        if (!stamp) return false;
        return (Date.now() - Number(stamp)) / (1000 * 60 * 60 * 24) < COOLDOWN_DAYS;
      } catch (e) { return false; }
    };
    const setCooldown = () => { try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) {} };
    const restricted = window.location.pathname.indexOf('/checkout') === 0 || window.location.pathname.indexOf('/account') === 0;
    if (inCooldown() || restricted) { root.remove(); return; }

    const form = root.querySelector('[data-modal-form]');
    const input = root.querySelector('[data-modal-input]');
    const submit = root.querySelector('[data-modal-submit]');
    const result = root.querySelector('[data-modal-result]');
    const backdrop = root.querySelector('[data-modal-backdrop]');
    const closeBtn = root.querySelector('[data-modal-close]');
    const declineBtn = root.querySelector('[data-modal-decline]');

    let triggered = false;
    const open = () => {
      if (triggered) return; triggered = true;
      root.setAttribute('data-open', 'true');
      root.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input && input.focus(), 250);
      clearTimeout(dwellTimer);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
    const close = (cool) => {
      root.setAttribute('data-open', 'false');
      root.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (cool !== false) setCooldown();
    };

    const dwellTimer = setTimeout(open, DWELL * 1000);
    const onMouseLeave = (e) => { if (e.clientY <= 0) open(); };
    if (window.matchMedia('(min-width: 768px)').matches) document.addEventListener('mouseleave', onMouseLeave);

    backdrop && backdrop.addEventListener('click', () => close(true));
    closeBtn && closeBtn.addEventListener('click', () => close(true));
    declineBtn && declineBtn.addEventListener('click', () => close(true));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.getAttribute('data-open') === 'true') close(true); });

    form && form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        result.textContent = 'Please enter a valid email.';
        result.setAttribute('data-state', 'error');
        return;
      }
      submit.setAttribute('data-loading', 'true');
      submit.disabled = true;
      try {
        await captureEmail(email, { source: 'modal-newsletter' });
        result.textContent = 'Got it. Code on its way.';
        result.setAttribute('data-state', 'success');
        setTimeout(() => close(true), 1500);
      } catch (err) {
        result.textContent = 'Something went wrong. Please try again.';
        result.setAttribute('data-state', 'error');
      } finally {
        submit.removeAttribute('data-loading');
        submit.disabled = false;
      }
    });
  }

  // ============================================================
  // Section: Creator videos (kol-videos)
  // Transform-based horizontal carousel. The "track" is translated
  // along X; the "rail" is the overflow:hidden viewport that clips it.
  // This avoids any conflict with page-level smooth-scroll libraries
  // (Lenis) and the native scroll-snap snap-back quirks.
  //   - rail_autoplay: timer advances translateX by one card; loops at end
  //   - hover_autoplay: hovering a card plays its muted video; leave = pause
  //   - prev/next arrows nudge translateX by ~3 cards
  //   - pointer drag (mouse + touch) for native-feeling swipe
  //   - lazy-mounts <source src> for any video card via IntersectionObserver
  //   - pauses both autoplay + active video when the section scrolls out
  //   - prefers-reduced-motion disables autoplay + transition
  // ============================================================
  function setupKolVideos(root) {
    if (!root) return;
    const rail = root.querySelector('[data-kv-rail]');
    const track = root.querySelector('[data-kv-track]');
    if (!rail || !track) return;

    const cards = [...track.querySelectorAll('[data-kv-card]')];
    if (!cards.length) return;

    const prevBtn = root.querySelector('[data-kv-prev]');
    const nextBtn = root.querySelector('[data-kv-next]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hoverAutoplay = root.dataset.hoverAutoplay === 'true' && !reduce;
    const railAutoplay = root.dataset.railAutoplay === 'true' && !reduce;
    const railInterval = (parseInt(root.dataset.railInterval, 10) || 5) * 1000;

    // --- Lazy-mount video <source src> when card nears the rail viewport ---
    const mountVideoSources = (card) => {
      const video = card.querySelector('[data-kv-video]');
      if (!video || video.dataset.mounted === 'true') return;
      video.querySelectorAll('source[data-kv-src]').forEach((s) => {
        s.setAttribute('src', s.dataset.kvSrc);
      });
      try { video.load(); } catch (e) {}
      video.dataset.mounted = 'true';
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) mountVideoSources(e.target); });
      }, { root: rail, rootMargin: '50% 0%', threshold: 0 });
      cards.forEach((c) => io.observe(c));
    } else {
      cards.forEach(mountVideoSources);
    }

    // --- External-URL → embed URL parser -------------------------
    // Supports TikTok, YouTube, Vimeo, Instagram Reels/posts.
    // Returns the embeddable iframe URL (with autoplay where supported)
    // or null if the input URL isn't recognised — in that case we fall
    // back to opening the original URL in a new tab.
    const parseEmbedUrl = (url) => {
      if (!url) return null;
      let m;
      m = url.match(/tiktok\.com\/.*\/video\/(\d+)/i);
      if (m) return 'https://www.tiktok.com/embed/v2/' + m[1];
      m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{11})/i);
      if (m) return 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1&playsinline=1&rel=0';
      m = url.match(/vimeo\.com\/(\d+)/i);
      if (m) return 'https://player.vimeo.com/video/' + m[1] + '?autoplay=1&playsinline=1';
      m = url.match(/instagram\.com\/(?:reel|p|tv)\/([\w-]+)/i);
      if (m) return 'https://www.instagram.com/p/' + m[1] + '/embed';
      return null;
    };

    const mountEmbed = (card) => {
      if (card.dataset.embedActive === 'true') return;
      const url = card.dataset.kvExternal;
      const embedSrc = parseEmbedUrl(url);
      if (!embedSrc) {
        // Unrecognised URL — open in a new tab so the user still reaches the content
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      const media = card.querySelector('.section-kol-videos__media');
      if (!media) return;
      const iframe = document.createElement('iframe');
      iframe.className = 'section-kol-videos__embed';
      iframe.src = embedSrc;
      iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('loading', 'lazy');
      iframe.setAttribute('title', card.querySelector('.section-kol-videos__caption-text')?.textContent || 'Creator video');
      media.appendChild(iframe);
      card.setAttribute('data-embed-active', 'true');
    };

    // --- Single-video-at-a-time playback (hover/click for MP4 cards) ---
    let playingCard = null;
    const pauseCard = (card) => {
      if (!card) return;
      const v = card.querySelector('[data-kv-video]');
      if (v) { v.pause(); try { v.currentTime = 0; } catch (e) {} }
      card.removeAttribute('data-playing');
    };
    const playCard = (card) => {
      if (!card || card === playingCard) return;
      mountVideoSources(card);
      pauseCard(playingCard);
      const v = card.querySelector('[data-kv-video]');
      if (!v) return;
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { card.setAttribute('data-playing', 'true'); playingCard = card; })
         .catch(() => { /* autoplay blocked — poster stays */ });
      } else {
        card.setAttribute('data-playing', 'true');
        playingCard = card;
      }
    };

    cards.forEach((card) => {
      const playBtn = card.querySelector('[data-kv-play]');
      const isExternal = !!card.dataset.kvExternal;
      if (playBtn) {
        playBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isExternal) { mountEmbed(card); return; }
          if (card === playingCard) { pauseCard(card); playingCard = null; }
          else playCard(card);
        });
      }
      // Hover-preview only applies to MP4 cards (external embeds are expensive
      // to load and would thrash on every hover).
      if (hoverAutoplay && !isExternal) {
        card.addEventListener('mouseenter', () => playCard(card));
        card.addEventListener('mouseleave', () => {
          if (card === playingCard) { pauseCard(card); playingCard = null; }
        });
      }
    });

    // --- Track position (translateX) ---
    let pos = 0;
    const cardStep = () => {
      const c = cards[0];
      if (!c) return 220;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '12') || 12;
      return c.getBoundingClientRect().width + gap;
    };
    const maxPos = () => Math.max(0, track.scrollWidth - rail.clientWidth);

    const setPos = (newPos, animate) => {
      pos = Math.max(0, Math.min(newPos, maxPos()));
      // During drag, the CSS rule `[data-dragging="true"] { transition: none }`
      // already disables transition — no per-frame inline manipulation needed.
      // Inline override is only required when we want an instant snap OUTSIDE
      // of a drag (e.g. resize handler), where data-dragging is not set.
      const isDragging = track.dataset.dragging === 'true';
      if (!animate && !isDragging) {
        track.style.transition = 'none';
        track.style.transform = `translate3d(${-pos}px, 0, 0)`;
        requestAnimationFrame(() => { track.style.transition = ''; });
      } else {
        track.style.transform = `translate3d(${-pos}px, 0, 0)`;
      }
      updateArrowState();
    };

    const advance = () => {
      if (maxPos() <= 0) return;
      if (pos >= maxPos() - 4) setPos(0, true);
      else setPos(pos + cardStep(), true);
    };
    const retreat = () => {
      if (pos <= 4) setPos(maxPos(), true);
      else setPos(pos - cardStep() * 3, true);
    };

    // --- Autoplay timer (paused on hover/focus/drag/out-of-viewport) ---
    let timer = null;
    const start = () => { if (!railAutoplay || timer) return; timer = setInterval(advance, railInterval); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    // Always kick autoplay off; IO below only PAUSES when the section leaves the viewport.
    start();

    if ('IntersectionObserver' in window) {
      const sectIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) start();
          else {
            stop();
            if (playingCard) { pauseCard(playingCard); playingCard = null; }
          }
        });
      }, { threshold: 0.1 });
      sectIo.observe(root);
    }

    // --- Arrows ---
    if (prevBtn) prevBtn.addEventListener('click', () => { stop(); retreat(); setTimeout(start, 1200); });
    if (nextBtn) nextBtn.addEventListener('click', () => { stop(); setPos(pos + cardStep() * 3, true); setTimeout(start, 1200); });

    function updateArrowState() {
      if (!prevBtn || !nextBtn) return;
      prevBtn.toggleAttribute('disabled', pos <= 1);
      nextBtn.toggleAttribute('disabled', pos >= maxPos() - 1);
    }
    window.addEventListener('resize', () => setPos(Math.min(pos, maxPos()), false), { passive: true });
    updateArrowState();

    // --- Pointer drag (mouse + touch) for native-feeling swipe ---
    // Smoothness strategy:
    //   1. Don't capture on pointerdown — wait for direction intent so vertical
    //      page scrolling isn't blocked on mobile.
    //   2. When horizontal intent is confirmed, RE-ANCHOR the drag origin to the
    //      current finger position. Otherwise the track would visibly jump by the
    //      threshold distance the moment we engage, breaking the feel of 1:1
    //      finger tracking.
    //   3. Pause autoplay on pointerdown (not just on capture) so a timer-driven
    //      advance can't fight a touch the user is already starting.
    //   4. Sample velocity over the last ~80ms of the drag and apply it as
    //      inertia on release. Without inertia, fast flicks feel "dead": the
    //      track halts exactly where the finger left it. Native horizontal
    //      scroll has built-in momentum — we recreate it manually.
    //   5. 5px threshold (down from 8) so the dead zone before engagement is
    //      barely perceptible without misfiring on jitter.
    let drag = null;
    const DRAG_THRESHOLD = 5;
    const INERTIA_MS = 280;          // how many ms of momentum to apply
    const VELOCITY_SAMPLE_MS = 80;   // recent window we average velocity over

    rail.addEventListener('pointerdown', (e) => {
      // Don't start drag from interactive children (play btn, shop link)
      if (e.target.closest('a, button')) return;
      drag = {
        startX: e.clientX,
        startY: e.clientY,
        startPos: pos,
        captured: false,
        pointerId: e.pointerId,
        // Velocity samples: array of {t, x} for the last VELOCITY_SAMPLE_MS
        samples: [{ t: performance.now(), x: e.clientX }]
      };
      // Pause autoplay as soon as the user touches — even if we end up
      // letting the page scroll (vertical intent), we don't want an
      // autoplay tick to land in the middle of the gesture.
      stop();
    });

    rail.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.captured) {
        // Direction not yet decided — both axes ambiguous, keep waiting
        // until one of them crosses the threshold.
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        if (absY > absX && absY > DRAG_THRESHOLD) {
          // User is scrolling vertically — abandon drag, page scroll wins.
          // Resume autoplay since we never engaged the carousel.
          drag = null;
          setTimeout(start, 1200);
          return;
        }
        if (absX > DRAG_THRESHOLD && absX > absY) {
          // Confirmed horizontal intent — re-anchor so motion is continuous
          // from THIS point (no visible jump from the threshold distance).
          drag.startX = e.clientX;
          drag.startPos = pos;
          drag.captured = true;
          track.setAttribute('data-dragging', 'true');
          try { rail.setPointerCapture(e.pointerId); } catch (err) {}
        } else {
          return; // still ambiguous, do nothing yet
        }
      }

      // Sample for velocity tracking
      const now = performance.now();
      drag.samples.push({ t: now, x: e.clientX });
      while (drag.samples.length > 1 && drag.samples[0].t < now - VELOCITY_SAMPLE_MS) {
        drag.samples.shift();
      }

      const liveDx = e.clientX - drag.startX;
      setPos(drag.startPos - liveDx, false);
    });

    const endDrag = (e) => {
      if (!drag) return;
      const wasCaptured = drag.captured;
      const samples = drag.samples;
      drag = null;
      if (!wasCaptured) {
        setTimeout(start, 1200);
        return;
      }
      track.removeAttribute('data-dragging');
      try { rail.releasePointerCapture(e.pointerId); } catch (err) {}

      // Compute release velocity from the recent samples (px/ms).
      // Negative velocity = finger moving left = user wants to advance.
      let velocity = 0;
      if (samples && samples.length >= 2) {
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        if (dt > 0) velocity = (last.x - first.x) / dt;
      }

      // Apply inertia: project where the track would naturally come to rest,
      // then snap to the nearest card boundary so the carousel always settles
      // on a card edge (not mid-card).
      const inertiaDx = velocity * INERTIA_MS;
      const projected = pos - inertiaDx; // moving track left if v negative
      const step = cardStep();
      const snapped = Math.max(0, Math.min(Math.round(projected / step) * step, maxPos()));
      setPos(snapped, true);
      setTimeout(start, 1500);
    };
    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);

    // Prevent images dragging interfering
    rail.addEventListener('dragstart', (e) => { e.preventDefault(); });
  }

  // ============================================================
  // Section: Announcement bar
  // Sitewide thin top strip. Multi-message rotation, dismissible
  // with 7-day expiry stored in localStorage (keyed by section id
  // so editing schema in Theme Editor doesn't clobber merchant data).
  // ============================================================
  function setupAnnouncementBar(root) {
    if (!root) return;

    const sectionId = root.dataset.sectionId || 'default';
    const storageKey = 'dymona.announcement.' + sectionId + '.dismissed';
    const dismissExpiryMs = 7 * 24 * 60 * 60 * 1000;

    // Honour an existing dismissal (within 7 days)
    try {
      const stamp = localStorage.getItem(storageKey);
      if (stamp && Date.now() - Number(stamp) < dismissExpiryMs) {
        root.remove();
        return;
      }
    } catch (e) { /* localStorage blocked — proceed without persistence */ }

    // Dismiss handler
    const closeBtn = root.querySelector('[data-ab-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        try { localStorage.setItem(storageKey, String(Date.now())); } catch (e) {}
        const h = root.offsetHeight;
        root.style.overflow = 'hidden';
        root.style.height = h + 'px';
        root.style.transition = 'height 280ms ' + 'cubic-bezier(0.4, 0, 0.2, 1)' + ', opacity 220ms';
        requestAnimationFrame(() => {
          root.style.height = '0px';
          root.style.opacity = '0';
        });
        setTimeout(() => root.remove(), 320);
      });
    }

    // Rotation (only if 2+ messages)
    const messages = [...root.querySelectorAll('[data-ab-message]')];
    if (messages.length <= 1) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = root.dataset.autoplay === 'true' && !reduce;
    const interval = (parseInt(root.dataset.interval, 10) || 6) * 1000;

    let current = messages.findIndex((m) => m.dataset.active === 'true');
    if (current < 0) current = 0;
    let timer = null;

    const advance = () => {
      const prev = current;
      current = (current + 1) % messages.length;
      messages[prev].removeAttribute('data-active');
      messages[prev].setAttribute('aria-hidden', 'true');
      messages[current].setAttribute('data-active', 'true');
      messages[current].removeAttribute('aria-hidden');
    };

    const start = () => { if (autoplay && !timer) timer = setInterval(advance, interval); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    start();
  }

  // ============================================================
  // Section registry + bootstrap
  // ============================================================
  // ============================================================
  // Section: Article — copy-link share button
  // ============================================================
  function setupArticle(root) {
    if (!root) return;
    const copyBtn = root.querySelector('[data-copy-link]');
    if (!copyBtn) return;
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        const original = copyBtn.getAttribute('aria-label');
        copyBtn.setAttribute('aria-label', 'Link copied');
        copyBtn.setAttribute('data-copied', 'true');
        setTimeout(() => {
          copyBtn.setAttribute('aria-label', original || 'Copy link');
          copyBtn.removeAttribute('data-copied');
        }, 1500);
      } catch (e) {
        // Fallback: select text via execCommand
        const ta = document.createElement('textarea');
        ta.value = window.location.href;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (err) {}
        document.body.removeChild(ta);
      }
    });
  }

  const REGISTRY = {
    'announcement-bar': setupAnnouncementBar,
    'header': setupHeader,
    'hero': setupHero,
    'value-trio': setupValueTrio,
    'component-story': setupComponentStory,
    'product-cards': setupProductCards,
    'kol-videos': setupKolVideos,
    'newsletter': setupNewsletter,
    'footer': () => {},
    'cart-drawer': setupCartDrawer,
    'modal-newsletter': setupNewsletterModal,
    'product-gallery': setupProductGallery,
    'product-info': setupProductInfo,
    'spec-table': setupSpecTable,
    'comparison': () => {},
    'reviews-placeholder': () => {},
    'faq-accordion': setupFaqAccordion,
    'article': setupArticle,
    'blog': () => {},
    'journal-featured': () => {}
  };

  function setupSections() {
    document.querySelectorAll('[data-section]').forEach((el) => {
      const name = el.getAttribute('data-section');
      const fn = REGISTRY[name];
      if (typeof fn === 'function') {
        try { fn(el); } catch (e) { console.error('[section "' + name + '"]', e); }
      }
    });
  }

  function bootstrap() {
    initLenis();
    initToasts();
    if (!(window.theme && window.theme.settings && window.theme.settings.show_demo_badge === false)) injectDemoBadge();
    setupSections();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap);
  else bootstrap();

  // Public API for inline scripts / Shopify section reloads in Theme Editor
  window.dymona = { cart, store, setupSections, money };

  // Re-init sections when Theme Editor adds/replaces a section
  document.addEventListener('shopify:section:load', (e) => {
    const sections = e.target.querySelectorAll('[data-section]');
    sections.forEach((el) => {
      const fn = REGISTRY[el.getAttribute('data-section')];
      if (typeof fn === 'function') try { fn(el); } catch (err) { console.error(err); }
    });
  });
})();
